import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Icon,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
  Spinner,
  Switch,
  Text,
  Textarea,
  useClipboard,
} from "@chakra-ui/react";
import { CopyIcon, WarningIcon } from "@chakra-ui/icons";
import { FaLeaf } from "react-icons/fa";
// The same icon the sidebar gives the requirement library, so the button points somewhere recognisable.
import { FiBookOpen } from "react-icons/fi";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useIntercom } from "react-use-intercom";
import { v4 as uuid } from "uuid";
import {
  AwardCriterionDto,
  AwardCriterionType,
  AwardSubCriterionAnswerFormat,
  type AwardSubCriterionDto,
  type LibraryElementContentDto,
  LibraryElementTypeDto,
  WeightType,
  type SourcingEventDto,
  type SourcingEventPatchDto,
} from "../../../../../autogen/bff-api";
import { isEditableInWizard } from "../../../../sourcing-event/changes/changeScope";
import { StepNavigation } from "../../../../../common/StepNavigation";
import { useConfirmationModal } from "../../../../../common/useConfirmationModal";
import { useEditor } from "../../../../../common/editing/save-coordinator";
import { FieldSaveError, FieldSaveErrorMessage, useFieldSaveError } from "../components/FieldSaveError";
import { AddedFromLibraryBanner } from "../../../../../common/library/AddedFromLibraryBanner";
import { AddRequirementPanel } from "../../../../../common/library/AddRequirementPanel";
import {
  awardCriterionToLibraryContent,
  awardSubCriterionToLibraryContent,
  fromLibraryCriterion,
} from "../../../../../common/library/awardLibraryContent";
import { useAddFromLibrary } from "../../../../../common/library/useAddFromLibrary";
import { useKeepUnderCriterionOption } from "../../../../../common/library/useKeepUnderCriterionOption";
import { useSaveToLibraryOption } from "../../../../../common/library/useSaveToLibraryOption";
import { useHasLibraryAwardCriterionOfType } from "../../../../../common/library/useLibraryAwardCriteriaOfType";
import { ApplyTemplateButton } from "../components/apply-template/ApplyTemplateButton";
import { useProcurementTerms } from "../../../../../common/terminology/useProcurementTerms";
import {
  activeExemptionType,
  AWARD_CRITERION_MODEL_OPTIONS,
  ENVIRONMENT_CRITERION_TYPES,
  ENVIRONMENT_EXEMPTION_TYPES,
  environmentRuleApplies,
  isEnvironmentCriterionType,
  isExemption,
  isExemptionMarker,
  MIN_ENVIRONMENT_WEIGHTING_PERCENT,
  withCriterionType,
  ENVIRONMENT_GROUP_VALUE,
  type AwardCriterionModel,
} from "../utils/publicProcurementOptions";
import {
  exemptionTemplateKey,
  exemptionTitleKey,
  isStandardText,
  templateBody,
  type EnvironmentJustificationTemplateKey,
} from "../utils/environmentJustificationTemplates";
import { AwardCriteriaTable } from "../components/AwardCriteriaTable";
import { AwardCriterionDrawer } from "../components/AwardCriterionDrawer";
import {
  AwardSubCriterionDrawer,
  type AwardSubCriterionFormValues,
} from "../components/AwardSubCriterionDrawer";
import { WeightSummary } from "../components/WeightSummary";
import { toCriteriaPatch } from "../utils/awardCriteriaPatch";
import { requiresBestPriceQualityRatio } from "../utils/procedure";
import {
  DEFAULT_BREAKPOINT_PERCENT,
  PRICE_MODEL_OPTIONS,
  priceModelKey,
  priceModelType,
  usePriceModelDescription,
  type PriceModelKey,
} from "../../../common/priceModel";

// t('Lowest price is a price-only award model')
// t('From the library')
// t('Delete criterion') t('Are you sure you want to delete this criterion?')
// t('Delete') t('Criteria are locked after publication')
// t('Climate and environmental weighting (anskaffelsesloven § 5b)')
// t('Exemption from climate and environmental requirements (anskaffelsesloven § 5c)')
// t('Climate and environmental considerations must be weighted at least {{percent}} % as an award criterion. You have weighted them {{weight}} %. To publish with a lower weighting you must document why.')
// t('You have chosen not to weight climate and environmental considerations. Select which exemption applies and document why.')
// t('Exemption reason') t('Justification for the exemption') t('sub-criteria')
// t('Set climate and environment weighting to 30%') t('Use an environment criterion from the library')
// t('Add an environment criterion') t('environment criterion')
// t('Justification for the lower weighting')
// t('Copy this justification into your konkurransegrunnlag. It is also added to the procurement protocol automatically.')
// t('Copy justification')
// t('No environmental weighting') t('Remove environment criteria')
// t('Any environment criteria you have registered will be removed if you continue.')
// t('This is still the standard text. Adapt it to your procurement — a copied template is not a valid justification.')

interface Props {
  eventId: string;
  previousStep?: () => void;
  nextStep?: () => void;
  nextStepLabel?: string;
  isActive: boolean;
}

// The library restricts itself to quality/environment; a procurement's award model also weights price
// and cost, so the panel's "write new" tab offers the wider set here.
const WIZARD_AWARD_TYPES: AwardCriterionType[] = [
  AwardCriterionType.Price,
  AwardCriterionType.Cost,
  AwardCriterionType.Quality,
  AwardCriterionType.QualityEnvironmentalCriteria,
];

type DrawerMode =
  | { kind: "criterion"; index: number }
  | { kind: "criterion-add" }
  | { kind: "sub-add"; criterionIndex: number }
  | { kind: "sub-library"; criterionIndex: number }
  | {
      kind: "sub-edit";
      criterionIndex: number;
      subIndex: number;
      initialValues: AwardSubCriterionFormValues;
    };

export const AwardCriteriaStep = ({ eventId, previousStep, nextStep, nextStepLabel, isActive }: Props) => {
  const { t } = useTranslation();
  const terms = useProcurementTerms();
  const { state, updateField, fieldStatus } = useEditor<SourcingEventDto, SourcingEventPatchDto>(eventId);
  const confirmationModal = useConfirmationModal();
  const [drawerState, setDrawerState] = useState<DrawerMode | null>(null);
  // Bumped on every open so the drawer's react-hook-form remounts with the row's values.
  const [drawerKey, setDrawerKey] = useState(0);
  const { update: updateIntercom } = useIntercom();

  useEffect(() => {
    if (!isActive) setDrawerState(null);
  }, [isActive]);

  const isDrawerOpen = drawerState !== null;
  useEffect(() => {
    updateIntercom({ hideDefaultLauncher: isDrawerOpen });
  }, [isDrawerOpen, updateIntercom]);

  const isLocked = !isEditableInWizard(state);
  const awardCriteria = state.eventData.awardCriteria ?? null;
  const boundToBestPriceQuality = requiresBestPriceQualityRatio(
    state.eventContext.type === "Public" ? state.eventContext.procedureType : undefined,
  );
  const selectedModel: AwardCriterionModel | null =
    (["lowestPrice", "bestPriceQualityRatio"] as const).find((k) => awardCriteria?.[k] != null) ??
    (boundToBestPriceQuality ? "bestPriceQualityRatio" : null);
  const awardSaving = fieldStatus("eventData.awardCriteria");
  const isSavingAwardCriteria = awardSaving === "saving" || awardSaving === "pending";
  const collectSaving = fieldStatus("eventData.collectSupplierPrice");
  const isSavingCollectSupplierPrice = collectSaving === "saving" || collectSaving === "pending";
  const priceModelSaving = fieldStatus("eventData.priceModel");
  const isSavingPriceModel = priceModelSaving === "saving" || priceModelSaving === "pending";
  const priceModelSaveError = useFieldSaveError(eventId, "eventData.priceModel");
  const selectedPriceModel = priceModelKey(state.eventData.priceModel);
  const breakpointPercent = state.eventData.priceModel?.hybrid?.breakpointPercent ?? DEFAULT_BREAKPOINT_PERCENT;
  const describePriceModel = usePriceModelDescription();

  const bestPriceQualityRatioCriteria: AwardCriterionDto[] = useMemo(
    () => awardCriteria?.bestPriceQualityRatio?.criteria ?? [],
    [awardCriteria]
  );

  // anskaffelsesloven § 5b/§ 5c: climate/environment must be weighted ≥ 30 % on Del II/III, or the
  // buyer must justify the deviation (spec route or a § 5c exemption) before publishing.
  const environmentWeight = useMemo(
    () =>
      bestPriceQualityRatioCriteria
        .filter((c) => ENVIRONMENT_CRITERION_TYPES.includes(c.type))
        .reduce((sum, c) => sum + (c.weight?.number ?? 0), 0),
    [bestPriceQualityRatioCriteria]
  );
  const environmentRuleInEffect = environmentRuleApplies(
    state.eventContext.type === "Public" ? state.eventContext.foaPart : undefined,
  );
  const needsEnvironmentJustification =
    environmentRuleInEffect && environmentWeight < MIN_ENVIRONMENT_WEIGHTING_PERCENT;

  const exemptionChosen = useMemo(
    () => isExemption(bestPriceQualityRatioCriteria),
    [bestPriceQualityRatioCriteria],
  );
  const activeExemption = useMemo(
    () => activeExemptionType(bestPriceQualityRatioCriteria),
    [bestPriceQualityRatioCriteria],
  );
  // Cards exclude the exemption marker — the exemption shows as the justification box, never a
  // criterion card — and so does the weight total the cards feed.
  const visibleCriteria = useMemo(
    () => bestPriceQualityRatioCriteria.filter((c) => !isExemptionMarker(c.type)),
    [bestPriceQualityRatioCriteria],
  );
  // Raw stored value distinguishes "never set" (null/undefined → safe to pre-fill) from "deliberately
  // cleared" ("" → leave empty); environmentJustification is the display/predicate string.
  const rawJustification = awardCriteria?.bestPriceQualityRatio?.environmentWeightingJustification;
  const environmentJustification = rawJustification ?? "";
  const { onCopy: copyJustification } = useClipboard(environmentJustification);

  // Which standard text the box pre-fills with: an active § 5c exemption uses that exemption's
  // template; otherwise the § 5b specification text (requirements in the kravspesifikasjon).
  const activeTemplateKey: EnvironmentJustificationTemplateKey = activeExemption
    ? exemptionTemplateKey(activeExemption)
    : "addressed-in-specification";
  const activeTemplateBody = templateBody(activeTemplateKey, t);
  // Same predicate as the saved flag and the BFF publish gate (any template body, language-aware) so the
  // inline warning and the publish block never disagree — e.g. pasting the other template, or switching
  // language between pasting and saving.
  const showForgotToEditWarning = isStandardText(environmentJustification, t);

  // Every write of the justification also sends whether it is still an unedited standard template
  // (derived here, against the live template) so the BFF publish gate can flag a copied boilerplate.
  const saveEnvironmentJustification = useCallback(
    (value: string | null, debounceMs = 0) =>
      updateField(
        {
          eventData: {
            awardCriteria: {
              bestPriceQualityRatio: {
                environmentWeightingJustification: value,
                environmentWeightingJustificationIsStandardText: value != null && isStandardText(value, t),
              },
            },
          },
        },
        { debounceMs },
      ),
    [updateField, t]
  );

  // A § 5c exemption is mutually exclusive with any weighted environment criterion, so it removes all
  // environment rows and stores a single exemption marker (the BFF fills its standard description),
  // pre-filling that exemption's template. Confirm first if any environment weighting would be discarded.
  const applyExemption = useCallback(
    (exemptionType: AwardCriterionType) => {
      const updated = bestPriceQualityRatioCriteria
        .filter((c) => !isEnvironmentCriterionType(c.type))
        .concat({
          type: exemptionType,
          name: "",
          description: "",
          weight: undefined,
        });
      return updateField({
        eventData: {
          awardCriteria: {
            bestPriceQualityRatio: {
              criteria: toCriteriaPatch(updated),
              environmentWeightingJustification: templateBody(exemptionTemplateKey(exemptionType), t),
              environmentWeightingJustificationIsStandardText: true,
            },
          },
        },
      });
    },
    [bestPriceQualityRatioCriteria, updateField, t],
  );

  // Switch which § 5c exemption applies (from the reason selector). The justification always follows the
  // chosen ground: it is reset to that ground's standard template so the protocol and the emitted notice
  // never name different § 5c grounds. If the buyer had adapted the justification, confirm first, since
  // switching discards that authored text.
  const switchExemption = useCallback(
    (exemptionType: AwardCriterionType) => {
      const applySwitch = () => {
        // Clear the marker's Doffin description so the BFF re-derives the standard text for the NEW ground.
        const updated = bestPriceQualityRatioCriteria.map((c) =>
          isExemptionMarker(c.type) ? { ...c, type: exemptionType, description: "" } : c,
        );
        void updateField({
          eventData: {
            awardCriteria: {
              bestPriceQualityRatio: {
                criteria: toCriteriaPatch(updated),
                environmentWeightingJustification: templateBody(exemptionTemplateKey(exemptionType), t),
                environmentWeightingJustificationIsStandardText: true,
              },
            },
          },
        });
      };
      const hasAdaptedJustification =
        environmentJustification !== "" && !isStandardText(environmentJustification, t);
      if (!hasAdaptedJustification) {
        applySwitch();
        return;
      }
      confirmationModal.show({
        title: t(exemptionTitleKey(exemptionType)),
        description: t("Your own justification will be replaced with the standard text for this exemption."),
        confirmButtonText: t("Replace justification") ?? "",
        onConfirmed: async () => {
          applySwitch();
        },
      });
    },
    [bestPriceQualityRatioCriteria, updateField, confirmationModal, environmentJustification, t],
  );

  // Bring the climate/environment weighting up to the 30 % floor straight from the warning box. Drops
  // any § 5c exemption marker, then: if an environment criterion already exists (the sub-30 % case),
  // bump the first one so the TOTAL weighting reaches 30 %; otherwise (coming from an exemption) add a
  // fresh criterion at 30 %. The BFF then classifies every environment row as a full award criterion
  // (vei A). Weight belongs on the criterion — the exemption codes never carry a weight (DFØ rule).
  const weightEnvironmentAtFloor = useCallback(() => {
    const nonMarker = bestPriceQualityRatioCriteria.filter((c) => !isExemptionMarker(c.type));
    const environmentRows = nonMarker.filter((c) => isEnvironmentCriterionType(c.type));
    let updated: AwardCriterionDto[];
    if (environmentRows.length === 0) {
      updated = nonMarker.concat({
        type: AwardCriterionType.QualityEnvironmentalCriteria,
        name: "",
        description: "",
        weight: { number: MIN_ENVIRONMENT_WEIGHTING_PERCENT, type: WeightType.Percentage },
      });
    } else {
      const othersWeight = environmentRows
        .slice(1)
        .reduce((sum, c) => sum + (c.weight?.number ?? 0), 0);
      const firstWeight = Math.max(0, MIN_ENVIRONMENT_WEIGHTING_PERCENT - othersWeight);
      let bumped = false;
      updated = nonMarker.map((c) => {
        if (isEnvironmentCriterionType(c.type) && !bumped) {
          bumped = true;
          return { ...c, weight: { number: firstWeight, type: WeightType.Percentage } };
        }
        return c;
      });
    }
    return updateField({
      eventData: { awardCriteria: { bestPriceQualityRatio: { criteria: toCriteriaPatch(updated) } } },
    });
  }, [bestPriceQualityRatioCriteria, updateField]);

  const chooseExemption = useCallback(
    (exemptionType: AwardCriterionType) => {
      // The exemption removes every environment row and overwrites the justification with the standard
      // template — so confirm first whenever that would discard work: any environment criterion (weighted
      // or not, but not the marker itself) or a buyer-adapted justification. The buyer picks which § 5c
      // ground applies from the menu; the reason selector lets them change it afterwards.
      const hasEnvironmentCriterion = bestPriceQualityRatioCriteria.some(
        (c) => isEnvironmentCriterionType(c.type) && !isExemptionMarker(c.type),
      );
      const hasAdaptedJustification =
        environmentJustification !== "" && !isStandardText(environmentJustification, t);
      if (!hasEnvironmentCriterion && !hasAdaptedJustification) {
        void applyExemption(exemptionType);
        return;
      }
      confirmationModal.show({
        title: t(exemptionTitleKey(exemptionType)),
        description:
          hasEnvironmentCriterion && hasAdaptedJustification
            ? t("Your environment criteria will be removed and your own justification replaced if you continue.")
            : hasEnvironmentCriterion
              ? t("Any environment criteria you have registered will be removed if you continue.")
              : t("Your own justification will be replaced with the standard text for this exemption."),
        confirmButtonText: hasEnvironmentCriterion
          ? (t("Remove environment criteria") ?? "")
          : (t("Replace justification") ?? ""),
        onConfirmed: async () => {
          await applyExemption(exemptionType);
        },
      });
    },
    [bestPriceQualityRatioCriteria, applyExemption, confirmationModal, environmentJustification, t],
  );

  // Standard-template management. When no justification is required (>= 30 % / rule N/A) drop only the
  // unedited standard text — a buyer-adapted justification is kept (hidden) so a transient bump to
  // >= 30 % and back does not destroy it. When required, pre-fill the standard text only if none was ever
  // set (rawJustification == null); a deliberately-cleared box ("") is left empty (the REQUIRED gate
  // reminds them) and survives a reload — it is never fought by a re-prefill.
  useEffect(() => {
    if (selectedModel !== "bestPriceQualityRatio" || isLocked) return;
    if (!needsEnvironmentJustification) {
      if (rawJustification && isStandardText(rawJustification, t)) saveEnvironmentJustification(null);
      return;
    }
    if (rawJustification == null) saveEnvironmentJustification(activeTemplateBody);
  }, [
    selectedModel,
    needsEnvironmentJustification,
    isLocked,
    rawJustification,
    activeTemplateBody,
    saveEnvironmentJustification,
    t,
  ]);

  const handleModelChange = useCallback(
    (model: AwardCriterionModel) => {
      // Both sides must appear in the patch: merge-patch keeps omitted keys, so the
      // BFF would see both lowestPrice and bestPriceQualityRatio set otherwise and
      // reject with IllegalArgumentException in AwardCriteriaDto.toDomain.
      if (model === "lowestPrice") {
        const switchToLowestPrice = () =>
          updateField({
            eventData: { awardCriteria: { lowestPrice: { selected: true }, bestPriceQualityRatio: null } },
          });
        if (visibleCriteria.length === 0) {
          switchToLowestPrice();
          return;
        }
        confirmationModal.show({
          title: t("Switch to lowest price?"),
          description: t("award_criteria_removed_on_lowest_price", { count: visibleCriteria.length }),
          confirmButtonText: t("Remove") ?? "Remove",
          onConfirmed: switchToLowestPrice,
        });
      } else if (model === "bestPriceQualityRatio") {
        updateField({
          eventData: {
            awardCriteria: { lowestPrice: null, bestPriceQualityRatio: {} },
          },
        });
      }
    },
    [updateField, visibleCriteria, confirmationModal, t]
  );

  const savePriceModel = useCallback(
    (model: PriceModelKey, percent: number, debounceMs = 0) =>
      // All variants must appear in the patch: merge-patch keeps omitted keys, so the
      // BFF would otherwise see several variants set and reject in PriceModelDto.toDomain.
      updateField(
        {
          eventData: {
            priceModel: {
              proportional: model === "proportional" ? { selected: true } : null,
              linear: model === "linear" ? { selected: true } : null,
              hybrid: model === "hybrid" ? { breakpointPercent: percent } : null,
            },
          },
        },
        { debounceMs },
      ),
    [updateField]
  );

  const writeCriteria = useCallback(
    (criteria: AwardCriterionDto[], debounceMs = 0) =>
      updateField({ eventData: { awardCriteria: { bestPriceQualityRatio: { criteria: toCriteriaPatch(criteria) } } } }, { debounceMs }),
    [updateField]
  );

  // Only one price criterion is allowed, so the panel's "write new" tab must not become a way around
  // the add menu. Every other type may be repeated.
  const availableAwardTypes = useMemo(
    () =>
      bestPriceQualityRatioCriteria.some((c) => c.type === AwardCriterionType.Price)
        ? WIZARD_AWARD_TYPES.filter((type) => type !== AwardCriterionType.Price)
        : WIZARD_AWARD_TYPES,
    [bestPriceQualityRatioCriteria],
  );

  const library = useAddFromLibrary({ items: bestPriceQualityRatioCriteria, commit: writeCriteria });
  const { dismiss } = library;

  // One per kind, so the two drawers do not share an answer: a criterion kept for reuse says nothing
  // about the requirement written under it next.
  const {
    canWriteToLibrary,
    option: criterionLibraryOption,
    reset: resetCriterionLibraryOption,
    saveIfChosen: saveCriterionIfChosen,
    save: saveCriterionNow,
    isInLibrary: criterionIsInLibrary,
  } = useSaveToLibraryOption(LibraryElementTypeDto.Award);
  const {
    option: subCriterionLibraryOption,
    reset: resetSubCriterionLibraryOption,
    saveIfChosen: saveSubCriterionIfChosen,
    save: saveSubCriterionNow,
    isInLibrary: subCriterionIsInLibrary,
  } = useSaveToLibraryOption(LibraryElementTypeDto.AwardSubCriterion);
  // The criterion a requirement is being written in, which is the one it can also be kept under.
  const criterionUnderEdit =
    drawerState != null && "criterionIndex" in drawerState
      ? (bestPriceQualityRatioCriteria[drawerState.criterionIndex] ?? null)
      : null;
  const {
    optionFor: keepUnderCriterionOptionFor,
    reset: resetKeepUnderCriterion,
    appendIfChosen: appendUnderCriterionIfChosen,
  } = useKeepUnderCriterionOption(criterionUnderEdit);
  const canKeepForReuse = canWriteToLibrary && !isLocked;

  // Every write but the batch add dismisses the confirmation — it describes a state the section has
  // moved on from.
  const saveCriteria = useCallback(
    (criteria: AwardCriterionDto[], debounceMs = 0) => {
      dismiss();
      return writeCriteria(criteria, debounceMs);
    },
    [writeCriteria, dismiss]
  );

  const handleCriterionChange = useCallback(
    (index: number, next: AwardCriterionDto) => {
      const prev = bestPriceQualityRatioCriteria[index];
      const updated = bestPriceQualityRatioCriteria.map((c, i) => (i === index ? next : c));
      // Every path out of here writes to the section, so the confirmation goes here rather than in
      // each branch — the § 5b one below patches the justification alongside the criteria and would
      // otherwise slip past `saveCriteria`, leaving an Undo that reverts more than it offers to.
      dismiss();
      // Crossing into a different § 5b/§ 5c path invalidates a standard justification written for the
      // previous one, so clear it in the same patch. We react only to a committed path change —
      // reclassifying away from the environment type, or a concrete new weight landing in another
      // range — never to the transient empty-weight state mid-typing (which would wipe text the user
      // is merely editing around; clearing to 0 % deliberately is the "Uvesentlig" template's job).
      const pathChanged =
        isEnvironmentCriterionType(prev.type) &&
        prev.type !== next.type &&
        (!isEnvironmentCriterionType(next.type) || next.weight?.number != null);
      // Only discard the boilerplate standard text on a path change — never a justification the buyer
      // has adapted, and never one another (still-weighted) environment criterion still needs.
      if (pathChanged && environmentJustification && isStandardText(environmentJustification, t)) {
        return updateField(
          {
            eventData: {
              awardCriteria: {
                bestPriceQualityRatio: {
                  criteria: toCriteriaPatch(updated),
                  environmentWeightingJustification: null,
                  environmentWeightingJustificationIsStandardText: false,
                },
              },
            },
          },
          { debounceMs: 300 },
        );
      }
      return saveCriteria(updated, 300);
    },
    [bestPriceQualityRatioCriteria, saveCriteria, dismiss, environmentJustification, updateField, t]
  );

  const handleSubCriterionChange = useCallback(
    (criterionIndex: number, subIndex: number, next: AwardSubCriterionDto) => {
      const updated = bestPriceQualityRatioCriteria.map((c, i) =>
        i === criterionIndex
          ? { ...c, subCriteria: (c.subCriteria ?? []).map((s, j) => (j === subIndex ? next : s)) }
          : c
      );
      return saveCriteria(updated, 300);
    },
    [bestPriceQualityRatioCriteria, saveCriteria]
  );

  const writeSubCriteria = useCallback(
    (criterionIndex: number, subCriteria: AwardSubCriterionDto[]) =>
      saveCriteria(
        bestPriceQualityRatioCriteria.map((c, i) => (i === criterionIndex ? { ...c, subCriteria } : c))
      ),
    [bestPriceQualityRatioCriteria, saveCriteria]
  );

  const openCriterion = useCallback((index: number) => setDrawerState({ kind: "criterion", index }), []);

  const openAddSubCriterion = useCallback(
    (criterionIndex: number) => {
      setDrawerKey((k) => k + 1);
      resetSubCriterionLibraryOption();
      resetKeepUnderCriterion();
      setDrawerState({ kind: "sub-add", criterionIndex });
    },
    [resetSubCriterionLibraryOption, resetKeepUnderCriterion]
  );

  const openSubCriterionLibrary = useCallback((criterionIndex: number) => {
    setDrawerKey((k) => k + 1);
    setDrawerState({ kind: "sub-library", criterionIndex });
  }, []);

  // Requirements picked from the library land straight in the criterion they were opened from — that is
  // what made them relevant enough to be offered. They arrive unweighted: what a requirement weighs is a
  // share of this criterion in this competition, and the table's row is where the buyer settles it.
  const addSubCriteriaFromLibrary = useCallback(
    async (criterionIndex: number, contents: LibraryElementContentDto[]) => {
      const picked = contents.flatMap((c) =>
        c.awardSubCriterion
          ? [
              {
                name: c.awardSubCriterion.name,
                description: c.awardSubCriterion.description,
                answerFormat: c.awardSubCriterion.answerFormat,
                requiresDocumentation: c.awardSubCriterion.requiresDocumentation,
              },
            ]
          : []
      );
      if (picked.length === 0) return;
      const existing = bestPriceQualityRatioCriteria[criterionIndex]?.subCriteria ?? [];
      await writeSubCriteria(criterionIndex, [...existing, ...picked]);
    },
    [bestPriceQualityRatioCriteria, writeSubCriteria]
  );

  const openEditSubCriterion = useCallback(
    (criterionIndex: number, subIndex: number) => {
      const sub = bestPriceQualityRatioCriteria[criterionIndex]?.subCriteria?.[subIndex];
      if (!sub) return;
      setDrawerKey((k) => k + 1);
      setDrawerState({
        kind: "sub-edit",
        criterionIndex,
        subIndex,
        initialValues: {
          name: sub.name ?? "",
          weight: sub.weight?.number != null ? String(sub.weight.number) : "",
          description: sub.description ?? "",
          answerFormat: sub.answerFormat ?? AwardSubCriterionAnswerFormat.FreeText,
          requiresDocumentation: sub.requiresDocumentation ?? false,
        },
      });
    },
    [bestPriceQualityRatioCriteria]
  );

  const handleSubCriterionSubmit = useCallback(
    async (values: Omit<AwardSubCriterionDto, "id">) => {
      if (drawerState?.kind !== "sub-add" && drawerState?.kind !== "sub-edit") return;
      const { criterionIndex } = drawerState;
      const existing = bestPriceQualityRatioCriteria[criterionIndex]?.subCriteria ?? [];
      const next =
        drawerState.kind === "sub-edit"
          ? existing.map((s, i) => (i === drawerState.subIndex ? { ...s, ...values } : s))
          : [...existing, values];
      await writeSubCriteria(criterionIndex, next);
      setDrawerState(null);
      // Only a newly written requirement is offered to the library, and only after it has landed in the
      // criterion — the section's copy is the one that must not be lost.
      // Two independent destinations, and the requirement may be sent to either, both or neither. Both
      // writes trail the one that put it in the criterion — that copy is the one that must not be lost.
      if (drawerState.kind === "sub-add") {
        const hostType = bestPriceQualityRatioCriteria[criterionIndex]?.type;
        if (hostType) {
          await saveSubCriterionIfChosen(awardSubCriterionToLibraryContent(values, hostType));
        }
        await appendUnderCriterionIfChosen(values);
      }
    },
    [
      drawerState,
      bestPriceQualityRatioCriteria,
      writeSubCriteria,
      saveSubCriterionIfChosen,
      appendUnderCriterionIfChosen,
    ]
  );

  const handleDeleteSubCriterion = useCallback(
    (criterionIndex: number, subIndex: number) => {
      confirmationModal.show({
        title: t("Delete sub-criterion"),
        description: t("Are you sure you want to delete this requirement?"),
        confirmButtonText: t("Delete") ?? "",
        confirmButtonColor: "red",
        onConfirmed: async () => {
          const existing = bestPriceQualityRatioCriteria[criterionIndex]?.subCriteria ?? [];
          await writeSubCriteria(
            criterionIndex,
            existing.filter((_, i) => i !== subIndex)
          );
        },
      });
    },
    [bestPriceQualityRatioCriteria, writeSubCriteria, confirmationModal, t]
  );

  // Opening the panel writes nothing: the criterion is collected whole and appended on save, so an
  // abandoned attempt leaves no row behind and no row is ever born without its description.
  const openCriterionAdd = useCallback(() => {
    setDrawerKey((k) => k + 1);
    resetCriterionLibraryOption();
    setDrawerState({ kind: "criterion-add" });
  }, [resetCriterionLibraryOption]);

  const submitCriterion = useCallback(
    async (next: AwardCriterionDto) => {
      setDrawerState(null);
      // The id is minted here rather than by the server: the row is keyed on it, so waiting for the save
      // to answer would remount the row and lose whatever the buyer does next.
      await saveCriteria([...bestPriceQualityRatioCriteria, { ...next, id: uuid() }]);
      await saveCriterionIfChosen(awardCriterionToLibraryContent(next));
    },
    [bestPriceQualityRatioCriteria, saveCriteria, saveCriterionIfChosen]
  );

  // The deliberate path, from a row that already exists: no checkbox, no confirmation — the button was
  // the choice, and the toast is the receipt.
  const saveCriterionToLibrary = useCallback(
    (index: number) => {
      const criterion = bestPriceQualityRatioCriteria[index];
      if (criterion) void saveCriterionNow(awardCriterionToLibraryContent(criterion));
    },
    [bestPriceQualityRatioCriteria, saveCriterionNow]
  );

  const saveSubCriterionToLibrary = useCallback(
    (criterionIndex: number, subIndex: number) => {
      const criterion = bestPriceQualityRatioCriteria[criterionIndex];
      const sub = criterion?.subCriteria?.[subIndex];
      if (criterion && sub) {
        void saveSubCriterionNow(awardSubCriterionToLibraryContent(sub, criterion.type));
      }
    },
    [bestPriceQualityRatioCriteria, saveSubCriterionNow]
  );

  const isInLibrary = useCallback(
    (kind: "criterion" | "sub-criterion", name: string | null | undefined) =>
      kind === "criterion" ? criterionIsInLibrary(name) : subCriterionIsInLibrary(name),
    [criterionIsInLibrary, subCriterionIsInLibrary]
  );

  // Whether the library has an environment criterion to offer at all. The § 5b block only shows the
  // button when it does — a button that opens an empty panel is worse than no button, since it reads
  // as a route the buyer failed to use rather than one that was never available.
  const [isEnvironmentLibraryOpen, setIsEnvironmentLibraryOpen] = useState(false);
  const hasEnvironmentCriterionInLibrary = useHasLibraryAwardCriterionOfType(
    AwardCriterionType.QualityEnvironmentalCriteria,
  );

  // Picking an environment criterion answers the § 5b duty, so it takes the same route the floor button
  // does: the exemption marker goes (the two never stand together), and the criterion arrives weighted.
  // withCriterionType settles the weight — the § 5b floor when the library criterion carries none, and
  // the library's own weight when it does, which is the same rule the type picker applies.
  const addEnvironmentCriterionFromLibrary = useCallback(
    (contents: LibraryElementContentDto[]) => {
      const picked = contents.flatMap((c) =>
        c.award ? [withCriterionType(fromLibraryCriterion(c.award), ENVIRONMENT_GROUP_VALUE)] : [],
      );
      if (picked.length === 0) return;
      // Through the library hook like every other add from the library, so this one is undoable too —
      // appended to the list with the exemption marker taken out, while Undo still restores the list
      // as it stood, exemption included.
      const nonMarker = bestPriceQualityRatioCriteria.filter((c) => !isExemptionMarker(c.type));
      library.add(picked, nonMarker);
    },
    [bestPriceQualityRatioCriteria, library],
  );

  // A batch is one edit, so Undo restores the list as it was rather than subtracting rows afterwards.
  const addFromLibrary = (contents: LibraryElementContentDto[]) =>
    library.add(contents.flatMap((c) => (c.award ? [fromLibraryCriterion(c.award)] : [])));


  const handleDelete = useCallback(
    (index: number) => {
      confirmationModal.show({
        title: terms.deleteCriterion,
        description: terms.deleteCriterionConfirm,
        confirmButtonText: t("Delete") ?? "",
        confirmButtonColor: "red",
        onConfirmed: async () => {
          // Never clear the § 5b/§ 5c justification on delete: at 0 % environment weighting it is still
          // required on Del II/III, and a buyer-adapted text must not be lost. Just save the criteria.
          await saveCriteria(bestPriceQualityRatioCriteria.filter((_, i) => i !== index));
        },
      });
    },
    [bestPriceQualityRatioCriteria, saveCriteria, confirmationModal, t]
  );

  const modelDescriptionKey = selectedModel
    ? AWARD_CRITERION_MODEL_OPTIONS.find((o) => o.value === selectedModel)?.descriptionKey
    : null;
  const modelHelperText = boundToBestPriceQuality
    ? t("FOA § 18-1 (8): a competitive dialogue or an innovation partnership is awarded on the best price-quality ratio alone")
    : modelDescriptionKey
      ? t(modelDescriptionKey)
      : null;

  // The three § 5c exemptions, labelled by their template title, for the reason selector.
  const exemptionOptions = ENVIRONMENT_EXEMPTION_TYPES.map((type) => ({
    type,
    labelKey: exemptionTitleKey(type),
  }));

  const environmentJustificationBlock = (
    <Alert status="warning" borderRadius="lg" my="3" alignItems="flex-start" flexDirection="column">
      <Flex>
        <AlertIcon />
        <AlertTitle>
          {exemptionChosen
            ? t("Exemption from climate and environmental requirements (anskaffelsesloven § 5c)")
            : t("Climate and environmental weighting (anskaffelsesloven § 5b)")}
        </AlertTitle>
      </Flex>
      <AlertDescription mt="2">
        {exemptionChosen
          ? t(
              "You have chosen not to weight climate and environmental considerations. Select which exemption applies and document why.",
            )
          : t(
              "Climate and environmental considerations must be weighted at least {{percent}} % as an award criterion. You have weighted them {{weight}} %. To publish with a lower weighting you must document why.",
              { percent: MIN_ENVIRONMENT_WEIGHTING_PERCENT, weight: environmentWeight },
            )}
      </AlertDescription>

      {exemptionChosen && (
        <FormControl mt="4" isDisabled={isLocked}>
          <FormLabel mb="2">{t("Exemption reason")}</FormLabel>
          <Select
            rounded="md"
            bg="smBackgroundPrimary"
            value={activeExemption ?? ""}
            isDisabled={isLocked}
            onChange={(e) => switchExemption(e.target.value as AwardCriterionType)}
          >
            {exemptionOptions.map((o) => (
              <option key={o.type} value={o.type}>{t(o.labelKey)}</option>
            ))}
          </Select>
        </FormControl>
      )}
      <Flex mt="3" gap="3" wrap="wrap">
        <Button
          size="sm"
          variant="outline"
          leftIcon={<FaLeaf />}
          isDisabled={isLocked}
          onClick={() => weightEnvironmentAtFloor()}
        >
          {t("Set climate and environment weighting to 30%")}
        </Button>
        {hasEnvironmentCriterionInLibrary && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={<Icon as={FiBookOpen} />}
            isDisabled={isLocked}
            onClick={() => setIsEnvironmentLibraryOpen(true)}
          >
            {t("Use an environment criterion from the library")}
          </Button>
        )}
      </Flex>

      <FormControl mt="4" isDisabled={isLocked}>
        <FormLabel mb="2">
          {exemptionChosen ? t("Justification for the exemption") : t("Justification for the lower weighting")}
        </FormLabel>
        <Textarea
          rounded="md"
          bg="smBackgroundPrimary"
          rows={5}
          value={environmentJustification}
          isDisabled={isLocked}
          onChange={(e) => saveEnvironmentJustification(e.target.value, 300)}
        />
        {showForgotToEditWarning && (
          <Flex align="center" gap="2" mt="2" color="smWarning">
            <WarningIcon boxSize="3.5" />
            <Text fontSize="sm">
              {t(
                "This is still the standard text. Adapt it to your procurement — a copied template is not a valid justification.",
              )}
            </Text>
          </Flex>
        )}
        <Flex align="center" justify="space-between" gap="2" mt="2">
          <FormHelperText mt="0">
            {t(
              "Copy this justification into your konkurransegrunnlag. It is also added to the procurement protocol automatically.",
            )}
          </FormHelperText>
          <Button
            size="xs"
            variant="ghost"
            leftIcon={<CopyIcon />}
            onClick={copyJustification}
            isDisabled={!environmentJustification}
          >
            {t("Copy justification")}
          </Button>
        </Flex>
      </FormControl>
    </Alert>
  );

  return (
    <Box pb="8" pt="10">
      <Flex justify="space-between" align="center" mb="6">
        <Heading as="h2" size="md">
          {terms.awardCriteria} {isSavingAwardCriteria && <Spinner size="xs" />}
        </Heading>
        <ApplyTemplateButton eventId={eventId} preselect={["award"]} />
      </Flex>
      <FieldSaveError eventId={eventId} path="eventData.awardCriteria" childrenShowTheirOwnErrors />

      {isLocked && (
        <Alert status="info" borderRadius="lg" mb="6">
          <AlertIcon />
          <AlertDescription>{terms.criteriaLockedAfterPublication}</AlertDescription>
        </Alert>
      )}

      {state.eventData.includeProducts ? (
        <Text fontSize="sm" color="smMuted" mb="6">
          {t("Suppliers are not asked for a separate total. The bid's total price is the sum of their product prices.")}
        </Text>
      ) : (
        <FormControl mb="6" isDisabled={isLocked}>
          <Flex align="center" justify="space-between" gap="4">
            <Box>
              <Flex align="center" gap="2">
                <FormLabel mb="0">{t("Ask suppliers to enter a price")}</FormLabel>
                {isSavingCollectSupplierPrice && <Spinner size="xs" />}
              </Flex>
              <FieldSaveError eventId={eventId} path="eventData.collectSupplierPrice" />
              <FormHelperText mt="1">
                {t(
                  "When off, suppliers are not asked for a total price. You enter or override prices yourself during evaluation.",
                )}
              </FormHelperText>
            </Box>
            <Switch
              isChecked={state.eventData.collectSupplierPrice}
              onChange={(e) => updateField({ eventData: { collectSupplierPrice: e.target.checked } })}
            />
          </Flex>
        </FormControl>
      )}

      <FormControl mb="6" isDisabled={isLocked}>
        <FormLabel>{terms.awardCriteriaModel}</FormLabel>
        <Select
          rounded="md"
          value={selectedModel ?? ""}
          isDisabled={isLocked || boundToBestPriceQuality}
          onChange={(e) => handleModelChange(e.target.value as AwardCriterionModel)}
        >
          {selectedModel === null && (
            <option value="" disabled>
              {t("Choose how the bids are evaluated")}
            </option>
          )}
          {AWARD_CRITERION_MODEL_OPTIONS.filter((opt) => !boundToBestPriceQuality || opt.value === "bestPriceQualityRatio").map(
            (opt) => (
              <option key={opt.value} value={opt.value}>
                {t(opt.labelKey)}
              </option>
            ),
          )}
        </Select>
        {modelHelperText && <FormHelperText>{modelHelperText}</FormHelperText>}
      </FormControl>

      {selectedModel === "lowestPrice" && (
        <Alert status="info" borderRadius="lg" mb="6">
          <AlertIcon />
          <AlertDescription>{t("Lowest price is a price-only award model")}</AlertDescription>
        </Alert>
      )}

      {selectedModel === "bestPriceQualityRatio" && (
        <>
          <FormControl mb="6" isDisabled={isLocked} isInvalid={!!priceModelSaveError}>
            <Flex align="center" gap="2">
              <FormLabel mb="0">{t("Price model")}</FormLabel>
              {isSavingPriceModel && <Spinner size="xs" />}
            </Flex>
            <Select
              rounded="md"
              mt="2"
              value={selectedPriceModel}
              onChange={(e) => savePriceModel(e.target.value as PriceModelKey, breakpointPercent)}
            >
              {PRICE_MODEL_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{t(opt.type)}</option>
              ))}
            </Select>
            {priceModelSaveError ? (
              <FieldSaveErrorMessage error={priceModelSaveError} />
            ) : (
              <FormHelperText>
                {describePriceModel(priceModelType(state.eventData.priceModel), breakpointPercent)}
              </FormHelperText>
            )}
          </FormControl>

          {selectedPriceModel === "hybrid" && (
            <FormControl mb="6" isDisabled={isLocked} maxW="64">
              <FormLabel>{t("Breakpoint (% above the lowest price)")}</FormLabel>
              <NumberInput
                min={1}
                max={99}
                step={1}
                precision={0}
                value={breakpointPercent}
                onChange={(_, value) => {
                  // Chakra clamps min/max only on blur, so skip out-of-range keystrokes
                  // (e.g. a half-typed "1" before "5") rather than PATCHing an invalid value.
                  const rounded = Math.round(value);
                  if (rounded >= 1 && rounded <= 99) savePriceModel("hybrid", rounded, 300);
                }}
              >
                <NumberInputField rounded="md" />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          )}

          {library.lastAdded && <AddedFromLibraryBanner count={library.lastAdded.count} onUndo={library.undo} />}

          {!isLocked && (
            <AddRequirementPanel
              isOpen={library.isPanelOpen}
              onClose={library.closePanel}
              type={LibraryElementTypeDto.Award}
              title={terms.addAwardCriteria}
              typeNoun={terms.awardCriteriaNoun}
              initialTab={library.tab}
              lockedToInitialTab
              awardTypes={availableAwardTypes}
              isAlreadyInSection={(el) =>
                !!el.content.award?.name && bestPriceQualityRatioCriteria.some((c) => c.name === el.content.award?.name)
              }
              onAdd={addFromLibrary}
            />
          )}
          {!isLocked && (
            <AddRequirementPanel
              isOpen={isEnvironmentLibraryOpen}
              onClose={() => setIsEnvironmentLibraryOpen(false)}
              type={LibraryElementTypeDto.Award}
              title={t("Add an environment criterion")}
              typeNoun={t("environment criterion")}
              initialTab="library"
              lockedToInitialTab
              criterionType={AwardCriterionType.QualityEnvironmentalCriteria}
              awardTypes={[AwardCriterionType.QualityEnvironmentalCriteria]}
              isAlreadyInSection={(el) =>
                !!el.content.award?.name && bestPriceQualityRatioCriteria.some((c) => c.name === el.content.award?.name)
              }
              onAdd={addEnvironmentCriterionFromLibrary}
            />
          )}
          <AwardCriteriaTable
            eventId={eventId}
            criteria={bestPriceQualityRatioCriteria}
            isLocked={isLocked}
            priceModel={state.eventData.priceModel}
            onCriterionChange={handleCriterionChange}
            onSubCriterionChange={handleSubCriterionChange}
            onOpenCriterion={openCriterion}
            onDeleteCriterion={handleDelete}
            onAddSubCriterion={openAddSubCriterion}
            onAddSubCriterionFromLibrary={openSubCriterionLibrary}
            onOpenSubCriterion={openEditSubCriterion}
            onDeleteSubCriterion={handleDeleteSubCriterion}
            onAddCriterion={openCriterionAdd}
            onAddCriterionFromLibrary={() => library.openPanel("library")}
            markedCount={library.markedCount}
            onSaveCriterionToLibrary={canKeepForReuse ? saveCriterionToLibrary : undefined}
            onSaveSubCriterionToLibrary={canKeepForReuse ? saveSubCriterionToLibrary : undefined}
            isInLibrary={canKeepForReuse ? isInLibrary : undefined}
          />
          <WeightSummary criteria={visibleCriteria} />

          {needsEnvironmentJustification && environmentJustificationBlock}
        </>
      )}

      <StepNavigation onPrevious={previousStep} onNext={nextStep} nextLabel={nextStepLabel} />

      <AwardCriterionDrawer
        key={drawerState?.kind === "criterion-add" ? `add-${drawerKey}` : "edit"}
        isOpen={drawerState?.kind === "criterion" || drawerState?.kind === "criterion-add"}
        eventId={eventId}
        mode={drawerState?.kind === "criterion-add" ? "add" : "edit"}
        index={drawerState?.kind === "criterion" ? drawerState.index : null}
        hasOtherPriceCriterion={
          drawerState?.kind === "criterion-add"
            ? bestPriceQualityRatioCriteria.some((c) => c.type === AwardCriterionType.Price)
            : drawerState?.kind === "criterion" &&
              bestPriceQualityRatioCriteria.some(
                (c, i) => i !== drawerState.index && c.type === AwardCriterionType.Price,
              )
        }
        isLocked={isLocked}
        criterion={drawerState?.kind === "criterion" ? bestPriceQualityRatioCriteria[drawerState.index] : null}
        onChange={(next) =>
          drawerState?.kind === "criterion" && handleCriterionChange(drawerState.index, next)
        }
        onSubmit={(next) => void submitCriterion(next)}
        saveToLibraryOption={criterionLibraryOption}
        onChooseExemption={environmentRuleInEffect ? chooseExemption : undefined}
        onClose={() => setDrawerState(null)}
      />

      {drawerState?.kind === "sub-library" && (
        <AddRequirementPanel
          // Every panel here counts the same drawerKey, so each has to say which one it is: two
          // siblings sharing a key leave React unable to tell them apart, and the one it settles on
          // is never unmounted — a panel that will not close however it is dismissed.
          key={`sub-library-${drawerKey}`}
          isOpen
          onClose={() => setDrawerState(null)}
          type={LibraryElementTypeDto.AwardSubCriterion}
          title={t("Add sub-criterion to {{criterion}}", {
            criterion:
              bestPriceQualityRatioCriteria[drawerState.criterionIndex]?.name?.trim() ||
              t(bestPriceQualityRatioCriteria[drawerState.criterionIndex]?.type ?? ""),
          })}
          typeNoun={t("sub-criteria")}
          initialTab="library"
          lockedToInitialTab
          criterionType={bestPriceQualityRatioCriteria[drawerState.criterionIndex]?.type}
          isAlreadyInSection={(el) =>
            !!el.content.awardSubCriterion?.name &&
            (bestPriceQualityRatioCriteria[drawerState.criterionIndex]?.subCriteria ?? []).some(
              (sub) => sub.name === el.content.awardSubCriterion?.name
            )
          }
          onAdd={(contents) => void addSubCriteriaFromLibrary(drawerState.criterionIndex, contents)}
        />
      )}

      <AwardSubCriterionDrawer
        key={`sub-${drawerKey}`}
        isOpen={drawerState?.kind === "sub-add" || drawerState?.kind === "sub-edit"}
        isSaving={isSavingAwardCriteria}
        mode={drawerState?.kind === "sub-edit" ? "edit" : "add"}
        criterionName={
          drawerState?.kind === "sub-add" || drawerState?.kind === "sub-edit"
            ? bestPriceQualityRatioCriteria[drawerState.criterionIndex]?.name?.trim() ||
              t(bestPriceQualityRatioCriteria[drawerState.criterionIndex]?.type ?? "")
            : ""
        }
        criterionType={
          drawerState?.kind === "sub-add" || drawerState?.kind === "sub-edit"
            ? bestPriceQualityRatioCriteria[drawerState.criterionIndex]?.type
            : undefined
        }
        defaultValues={drawerState?.kind === "sub-edit" ? drawerState.initialValues : undefined}
        // Left undefined rather than handing down a fragment of nothing: the fields render the
        // "Options" band whenever they are given children, and a bare heading is worse than none.
        libraryOptions={
          canWriteToLibrary
            ? ({ weight }) => (
                <>
                  {subCriterionLibraryOption}
                  {keepUnderCriterionOptionFor(weight)}
                </>
              )
            : undefined
        }
        onSubmit={handleSubCriterionSubmit}
        onClose={() => setDrawerState(null)}
      />

      {confirmationModal.modal}
    </Box>
  );
};
